<template>
    <div class="story-viewer-overlay">
        <div class="story-viewer-container">
            <!-- Progress bars -->
            <div class="progress-bars">
                <div 
                    v-for="(story, index) in stories" 
                    :key="story.id"
                    class="progress-bar-container"
                >
                    <div 
                        class="progress-bar" 
                        :class="{ 'active': index === currentIndex }"
                        :style="{ width: getProgressWidth(index) }"
                    ></div>
                </div>
            </div>
            
            <!-- Story content -->
            <div class="story-content">
                <img 
                    v-if="currentStory.media_type === 'image'"
                    :src="getMediaUrl(currentStory.media_path)"
                    class="story-media"
                />
                <video
                    v-else
                    :src="getMediaUrl(currentStory.media_path)"
                    class="story-media"
                    autoplay
                    loop
                    @ended="nextStory"
                ></video>
                
                <div class="story-caption" v-if="currentStory.caption">
                    {{ currentStory.caption }}
                </div>
            </div>
            
            <!-- User info -->
            <div class="user-info">
                <img :src="getAvatarUrl(user)" class="user-avatar" />
                <span class="username">{{ user.name }}</span>
                <span class="time-ago">{{ formatTimeAgo(currentStory.created_at) }}</span>
            </div>
            
            <!-- Close button -->
            <button class="close-button" @click="$emit('close')">
                <i class="fas fa-times"></i>
            </button>
            
            <!-- Navigation controls -->
            <div class="navigation-controls">
                <div class="prev-area" @click="prevStory"></div>
                <div class="next-area" @click="nextStory"></div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        user: Object,
        stories: Array,
        initialIndex: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            currentIndex: this.initialIndex,
            progress: 0,
            interval: null,
            videoDuration: 5 // Default duration for videos in seconds
        };
    },
    computed: {
        currentStory() {
            return this.stories[this.currentIndex];
        }
    },
    mounted() {
        this.startProgressTimer();
        document.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy() {
        this.clearProgressTimer();
        document.removeEventListener('keydown', this.handleKeyDown);
    },
    methods: {
        getMediaUrl(path) {
            return `/storage/${path}`;
        },
        getAvatarUrl(user) {
            // Replace with your actual avatar URL logic
            return user.avatar_url || '/default-avatar.jpg';
        },
        formatTimeAgo(timestamp) {
            const date = new Date(timestamp);
            const now = new Date();
            const diffInSeconds = Math.floor((now - date) / 1000);
            
            if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
            if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
            if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
            return `${Math.floor(diffInSeconds / 86400)}d ago`;
        },
        startProgressTimer() {
            this.progress = 0;
            const duration = this.currentStory.media_type === 'video' ? this.videoDuration * 1000 : 5000;
            const intervalTime = 50;
            const increment = (intervalTime / duration) * 100;
            
            this.interval = setInterval(() => {
                this.progress += increment;
                if (this.progress >= 100) {
                    this.nextStory();
                }
            }, intervalTime);
        },
        clearProgressTimer() {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
        },
        nextStory() {
            this.clearProgressTimer();
            this.$emit('view', this.currentStory.id);
            
            if (this.currentIndex < this.stories.length - 1) {
                this.currentIndex++;
                this.startProgressTimer();
            } else {
                this.$emit('close');
            }
        },
        prevStory() {
            this.clearProgressTimer();
            
            if (this.currentIndex > 0) {
                this.currentIndex--;
                this.startProgressTimer();
            } else {
                this.$emit('close');
            }
        },
        handleKeyDown(e) {
            if (e.key === 'ArrowRight') this.nextStory();
            if (e.key === 'ArrowLeft') this.prevStory();
            if (e.key === 'Escape') this.$emit('close');
        },
        getProgressWidth(index) {
            if (index < this.currentIndex) return '100%';
            if (index > this.currentIndex) return '0%';
            return `${this.progress}%`;
        }
    }
};
</script>

<style scoped>
.story-viewer-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
}

.story-viewer-container {
    position: relative;
    width: 100%;
    max-width: 400px;
    height: 80vh;
    border-radius: 10px;
    overflow: hidden;
}

.progress-bars {
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    display: flex;
    gap: 5px;
    z-index: 10;
}

.progress-bar-container {
    flex: 1;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
    overflow: hidden;
}

.progress-bar {
    height: 100%;
    background: white;
    transition: width 0.05s linear;
}

.story-content {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.story-media {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.story-caption {
    position: absolute;
    bottom: 60px;
    left: 0;
    right: 0;
    padding: 15px;
    color: white;
    text-align: center;
    background: rgba(0, 0, 0, 0.3);
}

.user-info {
    position: absolute;
    top: 20px;
    left: 15px;
    display: flex;
    align-items: center;
    color: white;
    z-index: 5;
}

.user-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    margin-right: 10px;
    border: 2px solid white;
}

.username {
    font-weight: bold;
    margin-right: 10px;
}

.time-ago {
    font-size: 12px;
    opacity: 0.8;
}

.close-button {
    position: absolute;
    top: 20px;
    right: 15px;
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    z-index: 5;
}

.navigation-controls {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
}

.prev-area {
    flex: 1;
    cursor: pointer;
}

.next-area {
    flex: 1;
    cursor: pointer;
}
</style>